import APIClient from '../../../lib/api';

class InvoiceService {
  // Get all invoices with filtering and pagination
  async getInvoices(params = {}) {
    const {
      page = 1,
      limit = 50,
      client_id,
      invoice_type,
      payment_status,
      date_from,
      date_to,
      search,
      sort_by = 'created_at',
      sort_order = 'desc',
      exclude_proforma
    } = params;

    const apiParams = {
      skip: (page - 1) * limit,
      limit,
      sort_by,
      sort_order
    };

    // Add optional filters
    if (client_id) apiParams.client_id = client_id;
    if (invoice_type) apiParams.invoice_type = invoice_type;
    if (payment_status) apiParams.payment_status = payment_status;
    if (date_from) apiParams.date_from = date_from;
    if (date_to) apiParams.date_to = date_to;
    if (search) apiParams.search = search;
    if (exclude_proforma !== undefined) apiParams.exclude_proforma = exclude_proforma;

    const response = await APIClient.invoices.getAll(apiParams);
    return response.data;
  }

  // Get single invoice by ID
  async getInvoice(id) {
    const response = await APIClient.invoices.getById(id);
    return response.data;
  }

  // Create new invoice
  async createInvoice(invoiceData) {
    const response = await APIClient.invoices.create(invoiceData);
    return response.data;
  }

  // Create invoice from selected services (Greek logic)
  async createInvoiceFromServices(serviceData) {
    const {
      client_id,
      service_ids,
      invoice_number,
      title,
      issue_date,
      due_date,
      notes
    } = serviceData;

    const params = {
      client_id: client_id.toString(),
      invoice_number,
      ...(title && { title }),
      ...(issue_date && { issue_date: issue_date.toISOString().split('T')[0] }),
      ...(due_date && { due_date: due_date.toISOString().split('T')[0] }),
      ...(notes && { notes })
    };

    const response = await APIClient.invoices.createFromServices(params, service_ids);
    return response.data;
  }

  // Update invoice
  async updateInvoice(id, invoiceData) {
    const response = await APIClient.invoices.update(id, invoiceData);
    return response.data;
  }

  // Update invoice with Greek calculations
  async updateInvoiceGreekCalculations(id, calculationData) {
    const {
      cost,
      vat_percentage = 24.0,
      invoice_type,
      service_type,
      service_name
    } = calculationData;

    const params = {
      cost: cost.toString(),
      vat_percentage: vat_percentage.toString(),
      ...(invoice_type && { invoice_type }),
      ...(service_type && { service_type }),
      ...(service_name && { service_name })
    };

    const response = await APIClient.invoices.updateGreekCalculations(id, params);
    return response.data;
  }

  // Get delete info (can_delete, reason, credit_notes_count)
  async getInvoiceDeleteInfo(id) {
    const response = await APIClient.invoices.getDeleteInfo(id);
    return response.data;
  }

  // Delete invoice
  async deleteInvoice(id) {
    const response = await APIClient.invoices.delete(id);
    return response.data;
  }

  // Update invoice payment
  async updateInvoicePayment(id, paymentData) {
    const response = await APIClient.invoices.updatePayment(id, paymentData);
    return response.data;
  }

  // Upload invoice to MyDATA (default: dev/test API για ασφάλεια)
  async uploadToMyData(id, mydataConfig = {}) {
    const {
      company_vat = '148060098',
      use_test_api = true
    } = mydataConfig;

    const params = {
      company_vat,
      use_test_api: use_test_api.toString()
    };

    const response = await APIClient.invoices.uploadToMyData(id, params);
    return response.data;
  }

  // Cancel MyDATA invoice (default: dev/test API για ασφάλεια)
  async cancelMyDataInvoice(id, mydataConfig = {}) {
    const {
      use_test_api = true
    } = mydataConfig;

    const params = {
      use_test_api: use_test_api.toString()
    };

    const response = await APIClient.invoices.cancelMyData(id, params);
    return response.data;
  }

  // Get invoice statistics
  async getInvoiceStatistics(params = {}) {
    const response = await APIClient.invoices.getStatistics(params);
    return response.data;
  }

  // Get Greek invoice statistics (alias for getInvoiceStatistics)
  async getGreekInvoiceStatistics(params = {}) {
    return this.getInvoiceStatistics(params);
  }

  // Get creditable invoices (for πιστωτικό συσχετιζόμενο)
  async getCreditableInvoices(clientId) {
    const response = await APIClient.invoices.getCreditable(clientId);
    return response.data;
  }

  // Create credit note from existing invoice
  async createCreditNote(referenceInvoiceId) {
    const response = await APIClient.invoices.createCredit(referenceInvoiceId);
    return response.data;
  }

  // Get overdue invoices
  async getOverdueInvoices() {
    const response = await APIClient.invoices.getOverdue();
    return response.data;
  }

  // Check invoice items for repair (incorrect total_price/vat_amount)
  async checkInvoiceItemsRepair() {
    try {
      const response = await APIClient.invoices.checkItemsRepair();
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('InvoiceService.checkInvoiceItemsRepair Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα ελέγχου InvoiceItems'
      };
    }
  }

  // Fix invoice items with incorrect calculations
  async fixInvoiceItemsRepair() {
    try {
      const response = await APIClient.invoices.fixItemsRepair();
      return {
        success: true,
        data: response.data,
        message: `Διορθώθηκαν ${response.data.fixed_count} InvoiceItems επιτυχώς`
      };
    } catch (error) {
      console.error('InvoiceService.fixInvoiceItemsRepair Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα διόρθωσης InvoiceItems'
      };
    }
  }

  // Check invoice items for missing quantity
  async checkInvoiceItemsQuantityRepair() {
    try {
      const response = await APIClient.invoices.checkItemsQuantityRepair();
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('InvoiceService.checkInvoiceItemsQuantityRepair Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα ελέγχου InvoiceItems quantity'
      };
    }
  }

  // Fix invoice items with missing quantity
  async fixInvoiceItemsQuantityRepair() {
    try {
      const response = await APIClient.invoices.fixItemsQuantityRepair();
      return {
        success: true,
        data: response.data,
        message: `Διορθώθηκαν ${response.data.fixed_count} InvoiceItems (quantity) επιτυχώς`
      };
    } catch (error) {
      console.error('InvoiceService.fixInvoiceItemsQuantityRepair Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα διόρθωσης InvoiceItems quantity'
      };
    }
  }

  // Check all invoice items for all repairs (comprehensive)
  async checkInvoiceItemsFullRepair() {
    try {
      const response = await APIClient.invoices.checkItemsFullRepair();
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('InvoiceService.checkInvoiceItemsFullRepair Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα ελέγχου InvoiceItems'
      };
    }
  }

  // Fix all invoice items (comprehensive repair)
  async fixInvoiceItemsFullRepair() {
    try {
      const response = await APIClient.invoices.fixItemsFullRepair();
      return {
        success: true,
        data: response.data,
        message: `Διορθώθηκαν ${response.data.total_repairs_count} InvoiceItems επιτυχώς (${response.data.quantity_repairs_count} quantity, ${response.data.price_repairs_count} prices)`
      };
    } catch (error) {
      console.error('InvoiceService.fixInvoiceItemsFullRepair Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα διόρθωσης InvoiceItems'
      };
    }
  }

  // Generate invoice PDF (placeholder - will be implemented)
  async generateInvoicePDF(id) {
    // This will be implemented with PDF generation library
    const response = await APIClient.invoices.generatePDF(id);
    return response.data;
  }

  // Format currency for Greek display
  formatCurrency(amount) {
    const n = Number(amount);
    const safeAmount = (amount != null && Number.isFinite(n)) ? n : 0;
    return new Intl.NumberFormat('el-GR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(safeAmount);
  }

  // Format date for Greek display
  formatDate(date) {
    return new Intl.DateTimeFormat('el-GR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(new Date(date));
  }

  // Format invoice number
  formatInvoiceNumber(number) {
    return number || 'N/A';
  }

  // Get payment status display
  getPaymentStatusDisplay(status) {
    const statusMap = {
      'unpaid': { text: 'Ανεξόφλητο', color: 'error' },
      'partial': { text: 'Μερικώς πληρωμένο', color: 'warning' },
      'paid': { text: 'Εξοφλημένο', color: 'success' },
      'overdue': { text: 'Ληξιπρόθεσμο', color: 'error' }
    };
    return statusMap[status] || { text: status, color: 'default' };
  }

  // Get invoice type display. includeReference: μόνο στο listing δείχνει "Πιστωτικό-53"
  getInvoiceTypeDisplay(type, invoice = null, includeReference = false) {
    const typeMap = {
      'invoice': { text: 'Τιμολόγιο', color: 'primary' },
      'receipt': { text: 'Απόδειξη', color: 'secondary' },
      'credit_note': { text: 'Πιστωτικό Τιμολόγιο', color: 'warning' },
      'cancel_invoice': { text: 'Πιστωτικό Τιμολόγιο', color: 'warning' },
      'service_invoice': {
        text: 'Τιμολόγιο Παροχής Υπηρεσιών με παρακρατηση',
        color: 'info'
      },
      'service_invoice_no_holdings': {
        text: 'Τιμολόγιο Παροχής Υπηρεσιών',
        color: 'info'
      },
      'sales_invoice': {
        text: 'Τιμολόγιο Πώλησης',
        color: 'primary'
      },
      'proforma_invoice': {
        text: 'Προτιμολόγιο',
        color: 'default'
      }
    };
    const normalized = (type || '').toString().toLowerCase();
    const info = typeMap[normalized] || { text: normalized || type || '-', color: 'default' };
    // Μόνο στο listing: "Πιστωτικό-53" με συσχετιζόμενο αριθμό
    if (includeReference && (normalized === 'cancel_invoice' || normalized === 'credit_note') && invoice?.reference_number) {
      return { ...info, text: `Πιστωτικό-${invoice.reference_number}` };
    }
    return info;
  }
}

const invoiceService = new InvoiceService();
export default invoiceService;
